import React from 'react'
import { styled, keyframes } from '@mui/material/styles'
import { ALLOWED_REACTION_EMOJI } from '../../../../types/reactions'

export interface Reaction {
  id: string
  emoji: string
  author?: string
}

const MAX_REACTION_AUTHOR_LENGTH = 24

function sanitizeReactionAuthor(author: string): string {
  const trimmed = author.trim()
  const codePoints = Array.from(trimmed)
  return codePoints.length > MAX_REACTION_AUTHOR_LENGTH
    ? `${codePoints.slice(0, MAX_REACTION_AUTHOR_LENGTH).join('')}…`
    : trimmed
}

interface ReactionOverlayProps {
  reactions: Reaction[]
}

export const ReactionOverlay: React.FC<ReactionOverlayProps> = ({ reactions }) => {
  return (
    <Overlay>
      {reactions.map((r) => (
        <ReactionBubble key={r.id}>
          <ReactionEmoji>{r.emoji}</ReactionEmoji>
          {r.author && <ReactionAuthorLabel>{sanitizeReactionAuthor(r.author)}</ReactionAuthorLabel>}
        </ReactionBubble>
      ))}
    </Overlay>
  )
}

interface ReactionBarProps {
  onReact: (emoji: string) => void
  style?: React.CSSProperties
}

export const ReactionBar: React.FC<ReactionBarProps> = ({ onReact, style }) => {
  const quickReactions = Array.from(ALLOWED_REACTION_EMOJI)
  return (
    <Bar style={style} data-reaction-bar>
      {quickReactions.map((emoji) => (
        <Button key={emoji} onClick={() => onReact(emoji)} title={emoji}>
          {emoji}
        </Button>
      ))}
    </Bar>
  )
}

/* styled */
const Overlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: 25,
})

const floatUp = keyframes({
  '0%': { opacity: 1, transform: 'translateY(0) scale(1)' },
  '100%': { opacity: 0, transform: 'translateY(-120px) scale(1.5)' },
})

const ReactionBubble = styled('div')({
  position: 'absolute',
  left: '50%',
  top: '80%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
  animation: `${floatUp} 1.6s ease-out forwards`,
  transform: 'translateX(-50%)',
})

const ReactionEmoji = styled('span')({
  fontSize: '32px',
  lineHeight: 1,
})

const ReactionAuthorLabel = styled('div')({
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1,
  padding: '2px 8px',
  borderRadius: '10px',
  maxWidth: '160px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'var(--sevv-text-primary, #fff)',
  background: 'var(--sevv-surface-overlay, rgba(0,0,0,0.55))',
})

const Bar = styled('div')({
  display: 'flex',
  gap: '8px',
})

const Button = styled('button')({
  background: 'rgba(0,0,0,0.5)',
  border: 'none',
  borderRadius: '50%',
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '18px',
  transition: 'transform 0.1s ease',
  color: '#fff',
  '&:hover': {
    transform: 'scale(1.2)',
    background: 'rgba(0,0,0,0.7)',
  },
  '&:active': { transform: 'scale(1)' },
}) 